// app/utils/amcUtils.ts
import React from 'react';
import cx from 'classnames';
import { Router } from '../configs/routes';
import { formatRoundedRupeeLiteral } from './commonUtils';
import { getReturnTimeHeader } from '.';
import FundAvatarNew from '../components/common/fundAvatarNew';
import Anchor from '../components/ui/Anchor';
import amcStyles from '../styles/amcs.scss';

interface Fund {
  fundName: string;
  imageUrl: string;
  currentNav: string;
  category: string;
  categoryFilter?: { categoryFilterValue: string }[];
  fundMetaData?: {
    slug: string;
    parentSlug: string;
    returns?: Record<string, any>;
    minAmount?: number;
    risk?: string;
    morningStarRating?: string;
    isinCode: string;
    aum: number;
  };
  planType?: string;
}

interface GetTableItemsParams {
  currentFunds: Fund[];
  selectedReturnTime: string;
  selectedTab: string;
  isMobile: boolean;
}

export const getTableItems = (params: GetTableItemsParams) => {
  const { currentFunds, selectedReturnTime, selectedTab, isMobile } = params;

  if (!currentFunds || !Array.isArray(currentFunds)) {
    return [];
  }

  return currentFunds.map((fund) => {
    const {
      fundName,
      imageUrl,
      currentNav,
      category,
      categoryFilter,
      fundMetaData,
      planType = 'GROWTH',
    } = fund;

    let returnYrVal;
    let risk;
    let morningStarRating;
    let isinCode;
    let slug;
    let parentSlug;
    let minAmount;
    let fundSize;
    let subCategory;

    if (fundMetaData) {
      slug = fundMetaData.slug;
      returnYrVal = fundMetaData.returns
        ? fundMetaData.returns[selectedReturnTime]
        : null;
      minAmount = fundMetaData.minAmount;
      risk = fundMetaData.risk;
      morningStarRating = fundMetaData.morningStarRating;
      isinCode = fundMetaData.isinCode;
      parentSlug = fundMetaData.parentSlug;
      fundSize = '₹' + formatRoundedRupeeLiteral(fundMetaData.aum);
    }

    risk = risk ? risk.slice(0, -4) : '--';
    returnYrVal = returnYrVal ? `${returnYrVal}%` : '--';
    morningStarRating = morningStarRating ? `${morningStarRating}` : 'NA';
    minAmount = minAmount ? `${minAmount}` : 'NA';
    subCategory = categoryFilter ? categoryFilter[0].categoryFilterValue : '--';

    const path = `/mutual-funds/${parentSlug}/${slug}/${isinCode}?planType=${planType}`;

    const anchorItem = {
      className: cx('lil-w-2/12', amcStyles['amc-align-right']),
      component: (
        <Anchor
          name="Invest now"
          className={`lil-text-white lil-bg-blue-300 lil-px-4 lil-py-2 lil-rounded-md lil-text-s-12 ${amcStyles['invest-now-style']}`}
          onClick={(e: any) => {
            e.preventDefault();
            e.stopPropagation();
            Router.pushRoute(`${path}`);
          }}
        />
      ),
    };

    const parseFundName = (fundName: string) => {
      const fundIndex = fundName.toLowerCase().indexOf('fund');

      if (fundIndex !== -1) {
        const partAfterFund = fundName.substring(fundIndex + 4);
        const hyphenIndex = partAfterFund.indexOf('-');

        if (hyphenIndex !== -1) {
          return (
            fundName.substring(0, fundIndex + 4) +
            partAfterFund.substring(0, hyphenIndex)
          ).trim();
        }
      }
      return fundName;
    };

    const webRowItems = [
      {
        className: 'lil-pl-6 lil-w-2/12',
        component: (
          <FundAvatarNew
            fundItem={{
              fundName,
              imageUrl,
              subTitle: `${category} | ${subCategory}`,
              fundMetaData: {
                slug,
                parentSlug,
                isinCode,
              },
            }}
            width={12}
            hideSubTitle={false}
            source="amc"
          />
        ),
      },
      ...(selectedTab === 'REGULAR'
        ? [
            {
              className: cx('lil-w-2/12', amcStyles['amc-align-right']),
              component: <div></div>,
            },
          ]
        : []),
      {
        className: 'lil-w-2/12',
        component: (
          <span className="lil-flex-col lil-items-center">
            <p className="lil-text-s-14">Risk</p>
            <p className="lil-text-black lil-text-s-14">{risk}</p>
          </span>
        ),
      },
      {
        className: 'lil-w-1/12',
        component: (
          <span className="lil-flex-col lil-items-center">
            <p className="lil-text-s-14">NAV</p>
            <p className="lil-text-black lil-text-s-14">
              {parseFloat(currentNav).toFixed(2)}
            </p>
          </span>
        ),
      },
      {
        className: 'lil-w-1/12',
        component: (
          <div>
            <p className="lil-text-s-14">Rating</p>
            <span className="lil-flex lil-items-center">
              {morningStarRating}
              {morningStarRating !== 'NA' && (
                <img
                  className="lil-pr-2 lil-pb-1 lil-pl-1"
                  src="https://assets1.cleartax-cdn.com/cleartax/images/1631780848_shape.svg"
                  alt="star"
                />
              )}
            </span>
          </div>
        ),
      },
      {
        className: 'lil-w-2/12',
        component: (
          <span className="lil-flex-col lil-items-center">
            <p className="lil-text-s-14">
              {getReturnTimeHeader(selectedReturnTime)}
            </p>
            <p className="lil-text-black lil-text-s-14">{returnYrVal}</p>
          </span>
        ),
      },
      {
        className: 'lil-w-2/12',
        component: (
          <span className="lil-flex-col lil-items-center">
            <p className="lil-text-s-14">Fund Size</p>
            <p className="lil-text-black lil-text-s-14">{fundSize}</p>
          </span>
        ),
      },
      ...(selectedTab === 'DIRECT' ? [anchorItem] : []),
    ];

    const mobileRowItems = [
      {
        className: 'lil-pl-6 lil-w-2/12',
        component: (
          <FundAvatarNew
            fundItem={{
              fundName: parseFundName(fundName),
              imageUrl,
              subTitle: `${category} | ${subCategory}${
                morningStarRating !== 'NA' ? ` | ${morningStarRating} Star` : ''
              }`,
              fundMetaData: {
                slug,
                parentSlug,
                isinCode,
              },
            }}
            width={true}
            columnWidth={250}
            hideSubTitle={false}
            source="amc"
          />
        ),
      },
      {
        className: 'lil-w-2/12',
        component: (
          <span className="lil-flex-col lil-items-center">
            <p className="lil-text-s-14">
              {getReturnTimeHeader(selectedReturnTime)}
            </p>
            <p className="lil-text-black lil-text-s-14">{returnYrVal}</p>
          </span>
        ),
      },
    ];

    return {
      rowItems: isMobile ? mobileRowItems : webRowItems,
      onRowClick: (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        Router.pushRoute(`${path}`);
      },
    };
  });
};

/**
 * Helper function to render the AMC fund table
 * @param params - Parameters for rendering the table
 * @returns Table row data
 */
export const getAmcTableItems = (params: {
  currentFunds: Fund[];
  selectedReturnTime: string;
  selectedTab: string;
  isMobile: boolean;
}) => {
  return getTableItems(params);
};



// WEBPACK FOOTER //
// ./utils/amcUtils.tsx